// ─── DSL Utils ────────────────────────────────────────────────────────────────
//
// Bidirectional conversion between the visual builder's internal state and
// the segment DSL stored in `definitionJson`.
//
// DSL format (backend):
//   { "eq":  ["$.customer.tier", "gold"] }
//   { "and": [{ "eq": [...] }, { "gte": [...] }] }
//   { "or":  [{ "eq": [...] }, { "eq": [...] }] }
//
// Internal state (builder):
//   BuilderState { logic: 'and', items: [Rule | SubGroup] }
//   SubGroup     { kind:'group', logic: 'and'|'or', rules: Rule[] }
//   Rule         { field, operator, value }
//
// Two-level limit: root items can be Rule or SubGroup;
// SubGroup items can only be Rules (no further nesting).

function uid(): string {
  return crypto.randomUUID().slice(0, 8);
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type Operator =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'contains'
  | 'starts_with'
  | 'ends_with'
  | 'in'
  | 'not_in';

export type FieldType =
  | 'text'
  | 'number'
  | 'boolean'
  | 'date'
  | 'email'
  | 'url'
  | 'select'
  | 'multiselect';

export interface FieldDef {
  key: string;
  label: string;
  fieldType: FieldType;
  options?: string[];
  isStandard?: boolean;
}

export interface Rule {
  id: string;
  field: string;
  operator: Operator;
  value: string | number | boolean | string[];
}

export interface SubGroup {
  kind: 'group';
  id: string;
  logic: 'and' | 'or';
  rules: Rule[];
}

export type RootItem = Rule | SubGroup;

export interface BuilderState {
  logic: 'and' | 'or';
  items: RootItem[];
}

export type ParseResult = { ok: true; state: BuilderState } | { ok: false };

// ─── Standard fields ──────────────────────────────────────────────────────────

export const STANDARD_FIELDS: FieldDef[] = [
  { key: 'tier', label: 'Tier', fieldType: 'text', isStandard: true },
  { key: 'totalPoints', label: 'Total Points', fieldType: 'number', isStandard: true },
  { key: 'lifetimePoints', label: 'Lifetime Points', fieldType: 'number', isStandard: true },
  {
    key: 'status',
    label: 'Status',
    fieldType: 'select',
    options: ['active', 'inactive', 'blocked'],
    isStandard: true,
  },
  { key: 'country', label: 'Country', fieldType: 'text', isStandard: true },
  { key: 'email', label: 'Email', fieldType: 'email', isStandard: true },
  { key: 'phone', label: 'Phone', fieldType: 'text', isStandard: true },
  { key: 'language', label: 'Language', fieldType: 'text', isStandard: true },
  { key: 'acquisitionSource', label: 'Acquisition Source', fieldType: 'text', isStandard: true },
];

// ─── Operator config ──────────────────────────────────────────────────────────

export const OPERATOR_LABELS: Record<Operator, string> = {
  eq: 'is',
  neq: 'is not',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  contains: 'contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
  in: 'is one of',
  not_in: 'is not one of',
};

const TEXT_OPS: Operator[] = ['eq', 'neq', 'contains', 'starts_with', 'ends_with', 'in', 'not_in'];
const NUMBER_OPS: Operator[] = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'];
const BOOL_OPS: Operator[] = ['eq'];
const DATE_OPS: Operator[] = ['eq', 'gt', 'gte', 'lt', 'lte'];
const SELECT_OPS: Operator[] = ['eq', 'neq', 'in', 'not_in'];
const MULTI_OPS: Operator[] = ['in', 'not_in'];

export function operatorsForField(fieldType: FieldType): Operator[] {
  switch (fieldType) {
    case 'number':
      return NUMBER_OPS;
    case 'boolean':
      return BOOL_OPS;
    case 'date':
      return DATE_OPS;
    case 'select':
      return SELECT_OPS;
    case 'multiselect':
      return MULTI_OPS;
    case 'email':
    case 'url':
    case 'text':
    default:
      return TEXT_OPS;
  }
}

// Whether an operator expects an array value (for "is one of" / "is not one of")
export function isArrayOperator(op: Operator): boolean {
  return op === 'in' || op === 'not_in';
}

// ─── Factory helpers ──────────────────────────────────────────────────────────

export function makeRule(field: string, fieldType: FieldType): Rule {
  const ops = operatorsForField(fieldType);
  const operator = ops[0];
  const value: Rule['value'] =
    fieldType === 'boolean'
      ? true
      : fieldType === 'number'
        ? 0
        : isArrayOperator(operator)
          ? []
          : '';
  return { id: uid(), field, operator, value };
}

export function makeSubGroup(): SubGroup {
  return { kind: 'group', id: uid(), logic: 'or', rules: [] };
}

export function emptyState(): BuilderState {
  return { logic: 'and', items: [] };
}

// ─── Build DSL from BuilderState ──────────────────────────────────────────────

function ruleToDsl(rule: Rule): Record<string, unknown> {
  const path = `$.customer.${rule.field}`;
  return { [rule.operator]: [path, rule.value] };
}

function groupToDsl(group: SubGroup): Record<string, unknown> {
  if (group.rules.length === 0) return {};
  const nodes = group.rules.map(ruleToDsl);
  if (nodes.length === 1) return nodes[0];
  return { [group.logic]: nodes };
}

export function buildDsl(state: BuilderState): Record<string, unknown> {
  const nonEmpty = state.items.filter((item) => {
    if (isSubGroup(item)) return item.rules.length > 0;
    return true;
  });

  if (nonEmpty.length === 0) return {};

  const nodes = nonEmpty.map((item) => (isSubGroup(item) ? groupToDsl(item) : ruleToDsl(item)));

  if (nodes.length === 1) return nodes[0];
  return { [state.logic]: nodes };
}

// ─── Parse DSL into BuilderState ──────────────────────────────────────────────

function isSubGroup(item: RootItem): item is SubGroup {
  return (item as SubGroup).kind === 'group';
}

const KNOWN_LEAF_OPS = new Set<string>([
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'contains',
  'starts_with',
  'ends_with',
  'in',
  'not_in',
]);

/** Try to parse a leaf DSL node into a Rule. Returns null if not parseable. */
function parseLeaf(node: Record<string, unknown>): Rule | null {
  const keys = Object.keys(node);
  if (keys.length !== 1) return null;
  const op = keys[0];
  if (!KNOWN_LEAF_OPS.has(op)) return null;

  const operands = node[op];
  if (!Array.isArray(operands) || operands.length !== 2) return null;

  const [pathRaw, value] = operands;
  if (typeof pathRaw !== 'string') return null;
  if (!pathRaw.startsWith('$.customer.')) return null;

  const field = pathRaw.slice('$.customer.'.length);
  if (!field) return null;

  return {
    id: uid(),
    field,
    operator: op as Operator,
    value: value as Rule['value'],
  };
}

/** Try to parse a DSL node as a SubGroup. Returns null if not parseable. */
function parseSubGroup(node: Record<string, unknown>): SubGroup | null {
  const keys = Object.keys(node);
  if (keys.length !== 1) return null;
  const logic = keys[0];
  if (logic !== 'and' && logic !== 'or') return null;

  const children = node[logic];
  if (!Array.isArray(children)) return null;

  const rules: Rule[] = [];
  for (const child of children) {
    if (typeof child !== 'object' || child === null) return null;
    const rule = parseLeaf(child as Record<string, unknown>);
    if (!rule) return null; // nested groups not supported at level 2
    rules.push(rule);
  }

  return { kind: 'group', id: uid(), logic: logic as 'and' | 'or', rules };
}

export function parseDsl(json: Record<string, unknown>): ParseResult {
  // Empty object → match all
  if (Object.keys(json).length === 0) {
    return { ok: true, state: emptyState() };
  }

  // Single leaf at root
  const singleLeaf = parseLeaf(json);
  if (singleLeaf) {
    return { ok: true, state: { logic: 'and', items: [singleLeaf] } };
  }

  // Top-level AND/OR
  const keys = Object.keys(json);
  if (keys.length !== 1) return { ok: false };
  const rootLogic = keys[0];
  if (rootLogic !== 'and' && rootLogic !== 'or') return { ok: false };

  const children = json[rootLogic];
  if (!Array.isArray(children)) return { ok: false };

  const items: RootItem[] = [];
  for (const child of children) {
    if (typeof child !== 'object' || child === null) return { ok: false };
    const c = child as Record<string, unknown>;

    // Try leaf first
    const leaf = parseLeaf(c);
    if (leaf) {
      items.push(leaf);
      continue;
    }

    // Try sub-group
    const group = parseSubGroup(c);
    if (group) {
      items.push(group);
      continue;
    }

    // Unsupported node (e.g. 'not', triple nesting)
    return { ok: false };
  }

  return { ok: true, state: { logic: rootLogic as 'and' | 'or', items } };
}
